import React, { useState } from 'react';
import { Info, ArrowRight, Workflow, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getTransformationStatusLabel, getTransformationStatusColor } from '@/types/transformation.types';

export const TransformationStatesLegend: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const transformationStates = [
    {
      status: 'pending',
      description: 'Transformación activa en proceso de ejecución, stock reservado',
      actions: ['Completar transformación', 'Eliminar orden'],
      whoCanAct: 'Operarios y supervisores'
    },
    {
      status: 'completed',
      description: 'Transformación finalizada exitosamente, stock actualizado y eficiencia calculada',
      actions: ['Solo consulta y reportes'],
      whoCanAct: 'Todos (solo lectura)'
    },
    {
      status: 'cancelled',
      description: 'Orden cancelada del sistema, stock liberado si fue reservado',
      actions: ['Solo consulta histórica'],
      whoCanAct: 'Solo supervisores'
    }
  ];

  return (
    <>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-blue-600 hover:text-blue-800 border-blue-200 hover:border-blue-300">
            <Info className="h-4 w-4 mr-2" />
            Estados y Flujo
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Info className="h-6 w-6 text-blue-600" />
              Estados de Transformación - Flujo Directo
            </DialogTitle>
            <DialogDescription className="text-base">
              Flujo directo de 3 estados: En Proceso, Completadas, Canceladas
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="diagram" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="diagram" className="flex items-center gap-2">
                <Workflow className="h-4 w-4" />
                Flujo de Estados
              </TabsTrigger>
              <TabsTrigger value="details" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Detalles y Explicación
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="diagram" className="space-y-6">
              {/* Flujo Visual Simplificado */}
              <Card className="border-blue-200 bg-blue-50">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg text-blue-900">Diagrama de Flujo Directo</CardTitle>
                  <p className="text-sm text-blue-700">Estados de transformación: En Proceso, Completada o Eliminada</p>
                </CardHeader>
                <CardContent>
                  {/* Flujo horizontal directo */}
                  <div className="flex items-center justify-center space-x-8">
                    {/* Estado 1: En Proceso */}
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center text-sm font-medium text-orange-600 mb-3">
                        ⚡
                      </div>
                      <Badge className={getTransformationStatusColor('pending')}>
                        {getTransformationStatusLabel('pending')}
                      </Badge>
                      <p className="text-xs text-gray-600 mt-2 text-center max-w-24">
                        Transformando insumos
                      </p>
                    </div>
                    
                    {/* Flechas de bifurcación */}
                    <div className="flex flex-col items-center space-y-2">
                      <div className="flex items-center">
                        <ArrowRight className="h-4 w-4 text-green-500" />
                      </div>
                      <p className="text-xs text-gray-500">o</p>
                      <div className="flex items-center">
                        <ArrowRight className="h-4 w-4 text-red-500" />
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-8">
                      {/* Estado 2a: Completada */}
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center text-sm font-medium text-green-600 mb-3">
                          ✓
                        </div>
                        <Badge className={getTransformationStatusColor('completed')}>
                          {getTransformationStatusLabel('completed')}
                        </Badge>
                        <p className="text-xs text-gray-600 mt-2 text-center max-w-24">
                          Transformación exitosa
                        </p>
                      </div>
                      
                      {/* Estado 2b: Eliminada */}
                      <div className="flex flex-col items-center">
                        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-sm font-medium text-red-600 mb-3">
                          ✗
                        </div>
                        <Badge className={getTransformationStatusColor('cancelled')}>
                          {getTransformationStatusLabel('cancelled')}
                        </Badge>
                        <p className="text-xs text-gray-600 mt-2 text-center max-w-24">
                          Orden cancelada
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="details" className="space-y-4">
              {/* Detalle de Estados */}
              <div className="grid gap-4">
                {transformationStates.map((state, index) => (
                  <Card key={state.status} className="border-l-4" style={{
                    borderLeftColor: 
                      state.status === 'pending' ? '#f97316' :
                      state.status === 'completed' ? '#10b981' :
                      '#dc2626'
                  }}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <Badge className={getTransformationStatusColor(state.status as any)}>
                              {getTransformationStatusLabel(state.status as any)}
                            </Badge>
                            <span className="text-sm font-medium text-gray-600">
                              Paso {index + 1}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 mb-3">{state.description}</p>
                          
                          <div className="grid md:grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-1">Acciones Disponibles:</p>
                              <div className="flex flex-wrap gap-1">
                                {state.actions.map((action, i) => (
                                  <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                    {action}
                                  </span>
                                ))}
                              </div>
                            </div>
                            <div>
                              <p className="text-xs font-medium text-gray-600 mb-1">Quién puede actuar:</p>
                              <span className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                                {state.whoCanAct}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end pt-4 border-t">
            <Button onClick={() => setIsOpen(false)}>
              Entendido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};